import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Breadcrumb } from '../../components/Breadcrumb';
import { PageTitle } from '../../components/PageTitle';
import { ContactSection } from '../features/ContactSection';

/**
 * ServiceCreativePage Component
 * Displays detailed information about the creative services offered by Lib-Gate
 */

const PAGETITLE_JP = 'クリエイティブ';
const PAGETITLE_EN = 'Creative';


const ServiceCreativePageContainer = styled.div.attrs({className: 'page-container'})`
  background: #F9FAFA;
  min-height: 100vh;
  padding: 0 0 170px;

  @media (max-width: 768px) {
    padding: 0 20px 95px;
`;


const CompanyOverviewSection = styled.section.attrs({className: 'company-overview-section'})`
  padding-top: 127px;
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);

  @media (max-width: 768px) {
    padding-top: 60px;
    margin: 0;
  }
`;

const CompanyOverviewTitle = styled.h2.attrs({className: 'company-overview-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0 0 90px 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 0 0 60px 0;
  }
`;

const CompanyOverviewContent = styled.div.attrs({className: 'company-overview-content'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 90px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const CompanyOverviewText = styled.div.attrs({className: 'company-overview-text'})`
  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: flex-end;

  @media (max-width: 768px) {
    gap: 30px;
    align-items: flex-start;
  }
`;

const CompanyOverviewQuote = styled.div.attrs({className: 'company-overview-quote'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  align-self: flex-start;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CompanyOverviewDescription = styled.div.attrs({className: 'company-overview-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  max-width: 610px;
  text-align: justify;

  p {
    margin: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
    max-width: none;
  }
`;

const CompanyOverviewImage = styled.picture.attrs({className: 'company-overview-image'})`
  display: block;
  aspect-ratio: 580/336;
  
  @media (max-width: 768px) {
    aspect-ratio: 1/1;
    border-radius: 0;
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);
  }

  img {
    width: 100%;
  }
`;

const CompanyApproachSection = styled.section.attrs({className: 'company-approach-section'})`
  padding-top: 151px;
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);

  @media (max-width: 768px) {
    padding-top: 60px;
    margin: 0;
  }
    .company-overview-image {
      @media (max-width: 768px) {
        order: 2;
      }
    }
`;

const CompanyApproachContent = styled.div.attrs({className: 'company-approach-content'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 90px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;


const CompanyApproachText = styled.div.attrs({className: 'company-approach-text'})`
  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const CompanyApproachQuote = styled.h3.attrs({className: 'company-approach-quote'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CompanyApproachDescription = styled.div.attrs({className: 'company-approach-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  text-align: justify;

  p {
    margin: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;

    p {
      margin: 0 0 15px 0;
    }
  }
`;

const ProductionSolutionsSection = styled.section.attrs({className: 'production-solution-section'})`
  padding-top: 151px;
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);

  @media (max-width: 768px) {
    padding-top: 120px;
    margin: 0;
  }
`;

const ProductionSolutionContainer = styled.div.attrs({className: 'production-solution-container'})`
  background: #FFFFFF;
  border-radius: 20px;
  padding: 100px 98px 90px;

  @media (max-width: 768px) {
    padding: 120px 20px;
    border-radius: 0;
    margin-left: -20px;
    margin-right: -20px;
    width: calc(100% + 40px);

  }
`;

const ProductionSolutionsTitle = styled.h2.attrs({className: 'production-solutions-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #0F3F8E;
  margin: 0 0 80px 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 28px;
    margin: 0 0 80px 0;
    color: #333333;
  }
`;

const ProductionSolutionsGrid = styled.div.attrs({className: 'production-solution-grid'})`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 90px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ProductionSolutionItem = styled.div.attrs({className: 'production-solution-item'})`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const ProductionSolutionTitle = styled.h3.attrs({className: 'production-solution-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75em;
  letter-spacing: 0.05em;
  color: #0F3F8E;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;

  }
`;

const ProductionSolutionDescription = styled.div.attrs({className: 'production-solution-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75em;
  color: #333333;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ProductionSolutionDivider = styled.hr.attrs({className: 'production-solution-divider'})`
  border: none;
  height: 1px;
  background: #0F3F8E;
  margin: 10px 0 0 0;
`;




const OtherSolutionsSection = styled.section.attrs({className: 'other-solutions-section'})`
  margin: 0 auto;
  max-width: var(--contents-maxwidth-pc);
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const OtherSolutionsTitle = styled.h2.attrs({className: 'other-solutions-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #0F3F8E;
  margin: 0 0 80px 0;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 9.6vw;
    margin: 0 0 40px 0;
  }
`;

const OtherSolutionsGrid = styled.div.attrs({className: 'other-solutions-grid'})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const OtherSolutionCard = styled(Link).attrs({className: 'other-solution-card'})`
  text-decoration: none;
  background: #FFFFFF;
  border-radius: 20px;
  padding: 50px;
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 768px) {
    padding: 30px 20px;
    gap: 20px;
  }
`;

const OtherSolutionTitle = styled.h3.attrs({className: 'other-solution-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.75;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const OtherSolutionDescription = styled.p.attrs({className: 'other-solution-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
  }
`;

const OtherSolutionButton = styled.div.attrs({className: 'other-solution-button'})`
  display: flex;
  align-items: center;
  gap: 20px;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.38;
  letter-spacing: 0.05em;
  color: #333333;
  text-decoration: none;
  transition: color 0.3s ease;
  align-self: flex-end;

  &:hover {
    color: #0F3F8E;
  }
`;



const ServiceCreativePage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceCreativePageContainer>
      {/* Breadcrumb Navigation */}
      <Breadcrumb 
        items={[
          { label: "事業紹介", path: "/service/" }, // 2階層目（リンクあり）
          { label: PAGETITLE_JP }           // 3階層目（現在のページなのでリンクなし）
        ]} 
      />


      {/* Page Title Section */}
      <PageTitle title={ PAGETITLE_JP } subtitle={ PAGETITLE_EN } />

      {/* Company Overview Section */}
      <CompanyOverviewSection>
        <CompanyOverviewTitle>リブゲートのクリエイティブについて</CompanyOverviewTitle>
        <CompanyOverviewContent>
          <CompanyOverviewText>
            <CompanyOverviewQuote>
              成功に導くための戦略とクリエイティブを<br className="pc-only" />提供します。
            </CompanyOverviewQuote>
            <CompanyOverviewDescription>
              <p>
                インターネットが生活の中心となった現在、目にするメディアは多岐に渡り、情報は溢れかえっています。
                その中で、消費者は「使いやすい」「わかりやすい」といった「体験」を重視するようになり、
                消費者行動のパターンが非常に複雑になっています。
              </p>
              <p>
                リブゲートは、その体験の本質をとらえ、最も効果的な施策・デザインは何なのかを考え、カタチにします。
                ただつくるだけではない、お客様の戦略パートナーとして企業のマーケティング活動を
                あらゆる角度からサポートします。
              </p>
            </CompanyOverviewDescription>
          </CompanyOverviewText>
          <CompanyOverviewImage>
            <source 
                  media="(min-width: 769px)" 
                  srcSet="/assets/images/service/creative/creative_01_pc.jpg" 
                />
              <img src="/assets/images/service/creative/creative_01_sp.jpg" alt="クリエイティブイメージ" />
          </CompanyOverviewImage>
        </CompanyOverviewContent>
      </CompanyOverviewSection>

      {/* Company Approach Section */}
      <CompanyApproachSection>
        <CompanyApproachContent>
        <CompanyOverviewImage>
            <source 
                  media="(min-width: 769px)" 
                  srcSet="/assets/images/service/creative/creative_02_pc.jpg" 
                />
              <img src="/assets/images/service/creative/creative_02_sp.jpg" alt="クリエイティブイメージ" />
          </CompanyOverviewImage>
          <CompanyApproachText>
            <CompanyApproachQuote>
              作って終わりではなく、<br />
              そこからが新しい可能性に向けてのスタートです。
            </CompanyApproachQuote>
            <CompanyApproachDescription>
              <p>
                弊社は、単なるデザイン制作に留まらず、クライアント様の事業成長を真に支援するパートナーとして、その成果を追求いたします。
              </p>
              <p>
                制作物の納品をもって業務完了とするのではなく、クライアント様の課題解決を起点とした戦略立案を重視。マーケティング戦略、SNSマーケティング、UI/UX改善といった包括的なウェブ戦略を、コンサルティングに留まらず、内製による制作実行まで一貫して提供することで、事業拡大に継続的に貢献してまいります。
              </p>
            </CompanyApproachDescription>
          </CompanyApproachText>
        </CompanyApproachContent>
      </CompanyApproachSection>

      {/* Production Solutions Section */}
      <ProductionSolutionsSection>
        <ProductionSolutionContainer>
        <ProductionSolutionsTitle>制作ソリューション</ProductionSolutionsTitle>
        <ProductionSolutionsGrid>
          <ProductionSolutionItem>
            <ProductionSolutionTitle>WEBプロモーション<ProductionSolutionDivider /></ProductionSolutionTitle>
            <ProductionSolutionDescription>
              ｜ 目標設定と戦略立案<br />
              ｜ ランディングページの設計<br />
              ｜ コンテンツ作成<br />
              ｜ フォームとCTA(Call to Action)<br />
              ｜ SEOとページ速度の最適化<br />
              ｜ A/Bテストと最適化<br />
              ｜ トラフィックの誘導<br />
              ｜ データの追跡と分析
            </ProductionSolutionDescription>
          </ProductionSolutionItem>

          <ProductionSolutionItem>
            <ProductionSolutionTitle>WEBサイト制作<ProductionSolutionDivider /></ProductionSolutionTitle>
            <ProductionSolutionDescription>
              ｜ 目標設定と戦略立案<br />
              ｜ ターゲットオーディエンスの定義<br />
              ｜ サイトマップとワイヤーフレーム作成<br />
              ｜ コンテンツの作成<br />
              ｜ デザイン<br />
              ｜ コーディング<br />
              ｜ SEO対策<br />
              ｜ テストとデバッグ<br />
              ｜ 公開<br />
              ｜ メンテナンス
            </ProductionSolutionDescription>
          </ProductionSolutionItem>

          <ProductionSolutionItem>
            <ProductionSolutionTitle>ブランディング<ProductionSolutionDivider /></ProductionSolutionTitle>
            <ProductionSolutionDescription>
              ｜ ブランドアセスメント<br />
              ｜ ターゲットオーディエンスの定義<br />
              ｜ ブランドのポジショニング<br />
              ｜ ブランドアイデンティティの開発<br />
              ｜ コンテンツ戦略の策定<br />
              ｜ チャネル選定<br />
              ｜ コミュニケーション戦略の構築<br />
              ｜ ブランドエクスペリエンスの設計<br />
              ｜ 評価と改善
            </ProductionSolutionDescription>
          </ProductionSolutionItem>

          <ProductionSolutionItem>
            <ProductionSolutionTitle>SNS戦略<ProductionSolutionDivider /></ProductionSolutionTitle>
            <ProductionSolutionDescription>
              ｜ 目標設定と戦略立案<br />
              ｜ ターゲットオーディエンスの定義<br />
              ｜ SNSプラットフォームの選定<br />
              ｜ コンテンツ戦略の策定<br />
              ｜ 企画の作成<br />
              ｜ コミュニティ構築とエンゲージメント<br />
              ｜ プロモーション活動<br />
              ｜ データ分析と改善
            </ProductionSolutionDescription>
          </ProductionSolutionItem>

          <ProductionSolutionItem>
            <ProductionSolutionTitle>オフライン制作<ProductionSolutionDivider /></ProductionSolutionTitle>
            <ProductionSolutionDescription>
              ｜ 印刷物デザイン<br />
              ｜ 広告媒体の制作<br />
              ｜ イベントブランディング<br />
              ｜ 展示会ブースデザイン<br />
              ｜ パッケージデザイン<br />
              ｜ ポイントオブセール(POS)デザイン<br />
              ｜ コーポレートイベントのデザイン<br />
              ｜ 車両デザイン
            </ProductionSolutionDescription>
          </ProductionSolutionItem>

          <ProductionSolutionItem>
            <ProductionSolutionTitle>UX/UIデザイン制作<ProductionSolutionDivider /></ProductionSolutionTitle>
            <ProductionSolutionDescription>
              ｜ デジタルプロダクトの戦略デザイン<br />
              ｜ 基幹システム・業務ツールの<br />
              　 UX/UI刷新<br />
              ｜ デジタルサービス・顧客接点の<br />
              　 UX/UI刷新<br />
              ｜ デザインシステム導入・運用支援と<br />
              　 開発連携<br />
              ｜ 上記に伴うフロントエンド実装
            </ProductionSolutionDescription>
          </ProductionSolutionItem>
        </ProductionSolutionsGrid>
        </ProductionSolutionContainer>
      </ProductionSolutionsSection>


      {/* Contact Section */}
      <ContactSection />

      {/* Other Solutions Section */}
      <OtherSolutionsSection>
        <OtherSolutionsTitle>他のソリューションを見る</OtherSolutionsTitle>
        <OtherSolutionsGrid>
          <OtherSolutionCard to="/service/development/">
            <OtherSolutionTitle>開発</OtherSolutionTitle>
            <OtherSolutionDescription>
              クライアントのビジネスを深く理解し、最適なソリューションを提供することで、課題解決と事業成長に貢献します。
            </OtherSolutionDescription>
            <OtherSolutionButton>
              詳しく見る
              <img src="/images/arrow-right.svg" alt="arrow right" />
            </OtherSolutionButton>
          </OtherSolutionCard >
          <OtherSolutionCard to="/service/product/">
            <OtherSolutionTitle>プロダクト</OtherSolutionTitle>
            <OtherSolutionDescription>
              デジタル領域の知見とものづくりへの情熱を融合し、バッグブランド「FADEN®︎」を展開しています。
            </OtherSolutionDescription>
            <OtherSolutionButton>
              詳しく見る
              <img src="/images/arrow-right.svg" alt="arrow right" />
            </OtherSolutionButton>
          </OtherSolutionCard>
        </OtherSolutionsGrid>
      </OtherSolutionsSection>

    </ServiceCreativePageContainer>
  );
};

export default ServiceCreativePage;
